import { useEffect, useRef, useState } from 'react'
import { useKeyboardShortcut, useMediaQuery } from '@w/utils-fe'
import { unknownErrorToError } from '@w/utils'
import { Command, CommandCollection, CommandInput, CommandItem, CommandList, CommandPanel } from '@w/ui/components/command'
import { Dialog, DialogPopup, DialogTitle, DialogClose } from '@w/ui/components/dialog'
import { SearchIcon } from '@w/ui/components/icon'
import { SidebarMenuButton, SidebarMenuItem } from '@w/ui/components/sidebar'
import { SpaceQueries, mergeSearchHits } from '../db/spaces-queries'
import { pageSearchOptions } from './page-search'
import { WorkspaceSearchFilters, type SearchPageScope } from './workspace-search-filters'
import type { SpaceResource } from '../router/spaces-resources'
import { useRouterContext } from '../router/flashcards-fe.router-bindings'
import { readRecentPages, recordRecentPage, recentPagesOptions, type RecentVisit } from './recent-pages'

type Hit = { pageId: string; spaceId: string; title: string; visitedAt?: number }

/** Search owns its panel/errors only. Authored writes and navigation keep their existing owners. */
export function WorkspaceSearch({ resources, workspaceId, currentPage, onOpenPage }: {
  resources: SpaceResource[]
  workspaceId: string
  currentPage?: { id: string; spaceId: string }
  onOpenPage: (pageId: string, spaceId: string) => Promise<void>
}) {
  const coarsePointer = useMediaQuery('(pointer: coarse)')
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [titleOnly, setTitleOnly] = useState(false)
  const [inPages, setInPages] = useState<SearchPageScope[]>([])
  const [navigationError, setNavigationError] = useState<Error | null>(null)
  const [recentVisits, setRecentVisits] = useState<RecentVisit[]>([])
  const [historyError, setHistoryError] = useState(false)
  const historyWriteFailed = useRef(false)
  const returnFocus = useRef<HTMLElement | null>(null)
  useEffect(() => {
    if (!open) returnFocus.current?.focus()
  }, [open])
  const ctx = useRouterContext()
  const userId = ctx.getUser().id
  const pageId = currentPage?.id
  const spaceId = currentPage?.spaceId
  useEffect(() => {
    if (!pageId || !spaceId) return
    try {
      recordRecentPage(ctx.localStorage, userId, workspaceId, { id: pageId, spaceId })
      historyWriteFailed.current = false
    } catch { historyWriteFailed.current = true }
  }, [ctx.localStorage, userId, workspaceId, pageId, spaceId])
  const navigationAttempt = useRef<symbol | null>(null)
  function changeOpen(value: boolean) {
    navigationAttempt.current = null
    setOpen(value)
  }
  function openSearch() {
    if (open) return
    returnFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
    setQuery('')
    setTitleOnly(false)
    setInPages([])
    setNavigationError(null)
    setHistoryError(historyWriteFailed.current)
    try { setRecentVisits(readRecentPages(ctx.localStorage, userId, workspaceId)) }
    catch { setRecentVisits([]); setHistoryError(true) }
    changeOpen(true)
  }
  useKeyboardShortcut({ p: event => {
    if (event.defaultPrevented || event.altKey || event.shiftKey || !(event.metaKey || event.ctrlKey)) return
    event.preventDefault()
    openSearch()
  } })

  async function openPage(hit: Hit) {
    if (navigationAttempt.current) return
    const attempt = Symbol('workspace search navigation')
    navigationAttempt.current = attempt
    setNavigationError(null)
    setOpen(false)
    try {
      await onOpenPage(hit.pageId, hit.spaceId)
      if (navigationAttempt.current === attempt) changeOpen(false)
    } catch (error) {
      if (navigationAttempt.current === attempt) {
        setOpen(true)
        setNavigationError(unknownErrorToError(error))
      }
    } finally {
      if (navigationAttempt.current === attempt) navigationAttempt.current = null
    }
  }

  function palette(hits: Hit[], message?: string, failed = false) {
    return <Command items={hits} itemToStringValue={item => (item as Hit).title} mode="none" value={query} onValueChange={value => { setQuery(value); setNavigationError(null) }}>
      <CommandPanel>
        <CommandInput aria-label="Search pages" placeholder="Search your pages…" data-testid="workspace-search-input" />
        {message ? <p className="text-on-surface-secondary px-4 py-3 text-sm" role={failed ? 'alert' : 'status'}>{message}</p> : null}
        {navigationError ? <p role="alert" className="text-error px-4 py-3 text-sm">Unable to open page: {navigationError.message}</p> : null}
        <CommandList><CommandCollection>{(hit: Hit) => <CommandItem key={`${hit.spaceId}:${hit.pageId}`} value={hit} aria-posinset={hits.indexOf(hit) + 1} aria-setsize={hits.length} data-testid={`workspace-search-result-${hit.pageId}`} onClick={() => { void openPage(hit) }}>
          <span className="min-w-0 flex-1 truncate">{hit.title.trim() || 'Untitled page'}</span>
          {hit.visitedAt === undefined ? null : <time data-testid={`workspace-search-visited-${hit.pageId}`} dateTime={new Date(hit.visitedAt).toISOString()} className="text-on-surface-secondary ml-auto text-xs">{new Date(hit.visitedAt).toLocaleString()}</time>}
        </CommandItem>}</CommandCollection></CommandList>
      </CommandPanel>
    </Command>
  }

  return <>
    <SidebarMenuItem><SidebarMenuButton className="rounded-lg" size="sm" data-testid="flashcards-open-search" onClick={openSearch}>
      <SearchIcon /><span>Search</span>{coarsePointer ? null : <kbd className="text-on-surface-secondary ml-auto text-xs">⌘/Ctrl P</kbd>}
    </SidebarMenuButton></SidebarMenuItem>
    <Dialog open={open} onOpenChange={changeOpen}>
      <DialogPopup finalFocus={false} className="flex max-w-xl flex-col gap-2 rounded-xl p-2">
        <div className="flex items-center justify-between px-2 pt-2"><DialogTitle>Search pages</DialogTitle><DialogClose data-testid="workspace-search-close" className="text-on-surface-secondary hover:text-on-surface rounded-md px-2 py-1 text-sm">Close</DialogClose></div>
        {open ? <WorkspaceSearchFilters resources={resources} titleOnly={titleOnly} onTitleOnlyChange={setTitleOnly} selected={inPages} onSelectedChange={setInPages} /> : null}
        {open ? query.trim().length > 0 ? <SpaceQueries resources={resources} options={resource => pageSearchOptions({ db: resource.db, spaceId: resource.space.id, query, titleOnly, inPageIds: inPages.map(page => page.pageId), mode: 'fulltext', limit: 20 })}>
          {results => {
            const error = results.find(result => result.error)?.error
            const hits = mergeSearchHits(results.flatMap(result => (result.data ?? []).map(hit => Object.assign({}, hit, { spaceId: result.resource.space.id }))), query, 20)
            const ready = results.every(result => result.data !== undefined)
            return error ? palette([], `Search failed: ${error.message}`, true) : palette(hits, ready && hits.length === 0 ? 'No pages found' : undefined)
          }}
        </SpaceQueries> : <SpaceQueries resources={resources} options={resource => recentPagesOptions(resource, recentVisits)}>
          {results => {
            const error = results.find(result => result.error)?.error
            const hits = recentVisits.flatMap(visit => {
              const page = results.find(result => result.resource.space.id === visit.spaceId)?.data?.find(row => row.pageId === visit.pageId)
              return page ? [{ ...page, spaceId: visit.spaceId, visitedAt: visit.visitedAt }] : []
            })
            return error ? palette([], `Search failed: ${error.message}`, true) : palette(hits, historyError ? 'Recent pages unavailable' : hits.length > 0 ? 'Recent pages' : 'Type to search your pages')
          }}
        </SpaceQueries> : null}
      </DialogPopup>
    </Dialog>
  </>
}
